<div class="glass-card rounded-2xl mb-6 overflow-hidden">
  <div class="p-6 border-b border-white/10 flex items-center justify-between">
    <h2 class="text-lg font-semibold text-text-primary">Fehlererfassung</h2>
    @if (editable()) {
      <button type="button" class="btn-primary flex items-center gap-2" [disabled]="isSubmitting()" (click)="addRow()">
        <span class="material-icons text-lg">add</span>
        Fehler hinzufügen
      </button>
    }
  </div>

  @if (!saved()) {
    <div class="p-6">
      <p class="text-sm text-text-muted">Bitte erstellen Sie den Auftragsbegleitschein zuerst, um Fehler zu erfassen.</p>
    </div>
  } @else if (!hasCustomer()) {
    <div class="p-6">
      <p class="text-sm text-text-muted">Ordnen Sie einen Kunden zu, damit Fehler erfasst werden können.</p>
    </div>
  } @else if (rows.length === 0) {
    <div class="p-6">
      <p class="text-sm text-text-muted">
        @if (editable()) {
          Noch keine Fehler erfasst. Nutzen Sie „Fehler erfassen“ an einer Position in der Zeiterfassung oder „Fehler hinzufügen“.
        } @else {
          Noch keine Fehler erfasst.
        }
      </p>
    </div>
  } @else {
    <div class="overflow-x-auto">
      <table class="w-full">
        <thead>
          <tr class="border-b border-white/10">
            <th class="px-2 py-3 text-left text-xs font-semibold text-text-secondary whitespace-nowrap">Pos. *</th>
            <th class="px-2 py-3 text-left text-xs font-semibold text-text-secondary whitespace-nowrap">Datum *</th>
            <th class="px-2 py-3 text-left text-xs font-semibold text-text-secondary whitespace-nowrap">Mitarbeiter (Pers.-Nr.) *</th>
            <th class="px-2 py-3 text-left text-xs font-semibold text-text-secondary whitespace-nowrap">Prüfer *</th>
            <th class="px-2 py-3 text-left text-xs font-semibold text-text-secondary whitespace-nowrap">Fehler *</th>
            <th class="px-2 py-3 text-left text-xs font-semibold text-text-secondary whitespace-nowrap">Stück *</th>
            <th class="px-2 py-3 text-left text-xs font-semibold text-text-secondary whitespace-nowrap">Bemerkung</th>
            @if (editable()) {
              <th class="px-2 py-3"></th>
            }
          </tr>
        </thead>
        <tbody>
          @for (row of rows; track row; let i = $index) {
            <tr [formGroup]="row" class="false-position-row border-b border-white/10">
              <!-- Pos. -->
              <td class="p-2 align-middle">
                <input
                  type="text"
                  formControlName="posNr"
                  class="w-full min-w-[48px] h-10 px-3 py-2 rounded-lg bg-white/8 border text-text-primary text-[0.8125rem] truncate transition-all focus:outline-none focus:border-primary"
                  [ngClass]="isFieldInvalid(row.get('posNr')) ? 'border-red-500 focus:border-red-500' : 'border-white/12'"
                  placeholder="z.B Pos. 1"
                  appSelectOnFocus
                />
              </td>

              <!-- Datum -->
              <td class="p-2 align-middle">
                <p-datepicker
                  formControlName="date"
                  dateFormat="dd.mm.yy"
                  [showIcon]="true"
                  [class.error]="isFieldInvalid(row.get('date'))"
                  appendTo="body"
                  styleClass="datepicker-component-style position-datepicker-style min-w-24"
                  panelStyleClass="datepicker-panel-style"
                ></p-datepicker>
              </td>

              <!-- Mitarbeiter (Pers.-Nr. of whoever did the faulty work) -->
              <td class="p-2 align-middle">
                <input
                  type="text"
                  formControlName="employeeNumber"
                  class="w-full min-w-[48px] h-10 px-3 py-2 rounded-lg bg-white/8 border text-text-primary text-[0.8125rem] truncate transition-all focus:outline-none focus:border-primary"
                  [ngClass]="isFieldInvalid(row.get('employeeNumber')) ? 'border-red-500 focus:border-red-500' : 'border-white/12'"
                  appSelectOnFocus
                />
              </td>

              <!-- Prüfer (Pers.-Nr. of whoever found the failure) -->
              <td class="p-2 align-middle">
                <input
                  type="text"
                  formControlName="inspectorNumber"
                  class="w-full min-w-[48px] h-10 px-3 py-2 rounded-lg bg-white/8 border text-text-primary text-[0.8125rem] truncate transition-all focus:outline-none focus:border-primary"
                  [ngClass]="isFieldInvalid(row.get('inspectorNumber')) ? 'border-red-500 focus:border-red-500' : 'border-white/12'"
                  appSelectOnFocus
                />
              </td>

              <!-- Fehler -->
              <td class="p-2 align-middle">
                <p-select
                  formControlName="code"
                  [options]="fehlerOptions"
                  optionLabel="label"
                  optionValue="value"
                  [filter]="true"
                  filterBy="label,value"
                  filterPlaceholder="Suchen..."
                  placeholder="Wählen"
                  [showClear]="false"
                  [class.error]="isFieldInvalid(row.get('code'))"
                  appendTo="body"
                  styleClass="select-component-style position-select-style"
                  panelStyleClass="select-component-panel-style"
                >
                  <ng-template pTemplate="item" let-option>
                    <div class="option-row">
                      <span class="option-code">{{ option.value }}</span>
                      <span class="option-label">{{ option.label }}</span>
                    </div>
                  </ng-template>
                  <ng-template pTemplate="selectedItem" let-selected> {{ selected.value }} – {{ selected.label }} </ng-template>
                </p-select>
              </td>

              <!-- Stück -->
              <td class="p-2 align-middle">
                <input
                  type="number"
                  formControlName="quantity"
                  class="w-full min-w-[48px] h-10 px-3 py-2 rounded-lg bg-white/8 border text-text-primary text-[0.8125rem] truncate transition-all focus:outline-none focus:border-primary [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:m-0 [&::-webkit-outer-spin-button]:m-0"
                  [ngClass]="isFieldInvalid(row.get('quantity')) ? 'border-red-500 focus:border-red-500' : 'border-white/12'"
                  min="1"
                  step="1"
                  appSelectOnFocus
                />
              </td>

              <!-- Bemerkung -->
              <td class="p-2 align-middle">
                <input
                  type="text"
                  formControlName="remark"
                  class="w-full min-w-[48px] h-10 px-3 py-2 rounded-lg bg-white/8 border border-white/12 text-text-primary text-[0.8125rem] truncate transition-all focus:outline-none focus:border-primary"
                  appSelectOnFocus
                />
              </td>

              <!-- Actions -->
              @if (editable()) {
                <td class="p-2 align-middle">
                  <div class="flex items-center gap-1">
                    <button
                      type="button"
                      class="p-2 rounded-lg text-text-muted hover:text-primary hover:bg-white/10 transition-all"
                      title="Zeile duplizieren"
                      [disabled]="isSubmitting()"
                      (click)="duplicateRow(i)"
                    >
                      <span class="material-icons text-lg">content_copy</span>
                    </button>
                    <button
                      type="button"
                      class="p-2 rounded-lg text-text-muted hover:text-red-400 hover:bg-white/10 transition-all"
                      title="Fehler entfernen"
                      [disabled]="isSubmitting()"
                      (click)="removeRow(i)"
                    >
                      <span class="material-icons text-lg">close</span>
                    </button>
                  </div>
                </td>
              }
            </tr>
          }
        </tbody>
      </table>
    </div>
  }
</div>
